import { useMemo } from 'react'
import { Calendar, Clock } from 'lucide-react'
import type { SurveyResponseItem } from '@bandos/shared'
import { format } from 'date-fns'
import {
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
  QuestionsGrid,
  MetaRow,
  MetaBadge,
} from '../../pages/SurveyInsightsPageStyles'
import { IndividualResponseCard } from './IndividualResponseCard'
import { getAnswerForQuestion } from '../../pages/surveyInsightsUtils'
import type { ReconciledQuestion } from '../../pages/surveyInsightsUtils'
import {
  useResponseNavigation,
  ResponseNavBar,
  ResponseHeader,
  ResponseTitle,
  ScrollPad,
} from '../../../../components/common/responseNavigation'

export interface IndividualResponsesViewProps {
  responses: SurveyResponseItem[]
  reconciledQuestions: ReconciledQuestion[]
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  return `~${Math.round(seconds / 60)}m`
}

export function IndividualResponsesView({
  responses,
  reconciledQuestions,
}: IndividualResponsesViewProps) {
  const total = responses.length
  const { currentIndex, goPrev, goNext } = useResponseNavigation(total)

  const currentResponse = total > 0 ? responses[currentIndex] : null

  const submittedLabel = useMemo(() => {
    if (!currentResponse) return null
    return format(new Date(currentResponse.submittedAt), 'MMM d, yyyy')
  }, [currentResponse])

  if (total === 0) {
    return (
      <EmptyState>
        <EmptyStateTitle>No responses yet</EmptyStateTitle>
        <EmptyStateText>Share the survey link to start collecting feedback.</EmptyStateText>
      </EmptyState>
    )
  }

  if (!currentResponse) return null

  return (
    <>
      <ResponseHeader>
        <ResponseTitle>
          Response {currentIndex + 1} of {total}
        </ResponseTitle>
        <MetaRow style={{ marginBottom: 0 }}>
          {submittedLabel && (
            <MetaBadge>
              <Calendar size={13} />
              {submittedLabel}
            </MetaBadge>
          )}
          {currentResponse.durationSeconds != null && currentResponse.durationSeconds > 0 && (
            <MetaBadge>
              <Clock size={13} />
              {formatDuration(currentResponse.durationSeconds)}
            </MetaBadge>
          )}
        </MetaRow>
      </ResponseHeader>

      <QuestionsGrid>
        {reconciledQuestions.map((question, i) => (
          <IndividualResponseCard
            key={question.id ?? i}
            question={question}
            questionIndex={i}
            answer={getAnswerForQuestion(currentResponse, question, i)}
            isDeleted={question.isDeleted}
          />
        ))}
      </QuestionsGrid>

      <ScrollPad />

      <ResponseNavBar
        currentIndex={currentIndex}
        total={total}
        onPrev={goPrev}
        onNext={goNext}
        itemNoun="response"
      />
    </>
  )
}
